import { X } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  headerVariant?: "default" | "gradient" | "plain";
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "full" | string;
  children: React.ReactNode;
  closeOnOverlayClick?: boolean;
  /** Fechar ao pressionar Escape. Independente de `closeOnOverlayClick`. */
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  footer?: React.ReactNode;
}

/**
 * Pilha dos modais abertos: o Esc fecha só o do topo. Sem ela, um modal de confirmação
 * aberto sobre outro fechava os dois (cada um ouvia o Esc na janela).
 */
const openModalStack: number[] = [];
let nextModalId = 1;

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerVariant = "gradient",
  maxWidth = "lg",
  children,
  closeOnOverlayClick = false,
  closeOnEscape = true,
  showCloseButton = true,
  footer,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const modalIdRef = useRef(0);
  if (modalIdRef.current === 0) modalIdRef.current = nextModalId++;

  useEffect(() => {
    if (!open) return;
    const id = modalIdRef.current;
    openModalStack.push(id);
    return () => {
      const index = openModalStack.lastIndexOf(id);
      if (index !== -1) openModalStack.splice(index, 1);
    };
  }, [open]);

  // Fechar com ESC — um único ouvinte (antes havia também o do diálogo, e o onClose
  // rodava duas vezes) e só para o modal do topo da pilha.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !closeOnEscape) return;
      if (openModalStack[openModalStack.length - 1] !== modalIdRef.current)
        return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, closeOnEscape]);

  // Foco automático e prevenir scroll do body
  useEffect(() => {
    if (!open) return;
    // Só o primeiro modal da pilha trava a rolagem; o último a fechar destrava.
    if (document.body.style.overflow !== "hidden") {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const focusTimer = setTimeout(() => {
      if (dialogRef.current) {
        dialogRef.current.focus();
      }
    }, 100);
    return () => {
      clearTimeout(focusTimer);
      if (openModalStack.length === 0) {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    };
  }, [open]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!open) return null;

  const maxWidthClasses: Record<string, string> = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    full: "max-w-full mx-4",
    "max-w-2xl": "max-w-2xl",
    "max-w-3xl": "max-w-3xl",
    "max-w-4xl": "max-w-4xl",
    "max-w-6xl": "max-w-6xl",
    "max-w-7xl": "max-w-7xl",
    "max-w-md": "max-w-md",
    "max-w-lg": "max-w-lg",
  };

  const headerClasses =
    headerVariant === "gradient"
      ? "bg-linear-to-r from-purple-800 via-indigo-700 to-blue-700 text-white border-b-0 shadow-lg shadow-purple-900/10"
      : "bg-white border-b border-purple-200/50";

  const titleClasses =
    headerVariant === "gradient"
      ? "text-white drop-shadow-xs"
      : "text-gray-900";

  const subtitleClasses =
    headerVariant === "gradient"
      ? "text-purple-100 opacity-90"
      : "text-gray-400 font-medium";

  const closeButtonClasses =
    headerVariant === "gradient"
      ? "text-white/70 hover:text-white hover:bg-white/20 active:bg-white/30"
      : "text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100";

  const content = (
    <div
      ref={overlayRef}
      className="fixed z-9999 flex items-center justify-center bg-gray-900/60 backdrop-blur-md animate-fade-in"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 0,
        padding: 0,
      }}
      aria-modal="true"
      role="dialog"
      aria-labelledby={title ? "modal-title" : undefined}
      aria-describedby={subtitle ? "modal-subtitle" : undefined}
      tabIndex={-1}
      onClick={handleOverlayClick}
    >
      <div
        ref={dialogRef}
        className={cn(
          "bg-white rounded-none sm:rounded-3xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] w-full h-full sm:h-auto outline-hidden animate-modal-scale-in",
          "max-h-screen sm:max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col",
          "border-0 sm:border border-purple-200",
          "m-0 sm:m-4",
          maxWidthClasses[maxWidth || "lg"] || maxWidth || maxWidthClasses.lg,
        )}
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || subtitle || showCloseButton) && (
          <div
            className={cn(
              "w-full px-6 py-4 shrink-0 sticky top-0 z-10",
              headerClasses,
            )}
          >
            <div className="flex items-start justify-between gap-3 w-full">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {icon && (
                  <div
                    className={cn(
                      "shrink-0 p-2.5 rounded-xl shadow-inner",
                      headerVariant === "gradient"
                        ? "bg-white/10"
                        : "bg-indigo-50",
                    )}
                  >
                    <div
                      className={
                        headerVariant === "gradient"
                          ? "text-white"
                          : "text-indigo-600"
                      }
                    >
                      {icon}
                    </div>
                  </div>
                )}
                <div className="flex-1 min-w-0 pt-0.5">
                  {title && (
                    <div
                      id="modal-title"
                      className={cn(
                        "text-lg sm:text-xl font-black leading-tight tracking-tight wrap-break-word",
                        titleClasses,
                      )}
                    >
                      {title}
                    </div>
                  )}
                  {subtitle && (
                    <div
                      id="modal-subtitle"
                      className={cn(
                        "mt-0.5 text-[10px] sm:text-xs tracking-wide wrap-break-word",
                        subtitleClasses,
                      )}
                    >
                      {subtitle}
                    </div>
                  )}
                </div>
              </div>
              {showCloseButton && (
                <button
                  onClick={onClose}
                  aria-label="Fechar modal"
                  className={cn(
                    "shrink-0 p-1.5 rounded-xl transition-all duration-300 focus:outline-hidden focus:ring-2 focus:ring-purple-400 active:scale-90",
                    closeButtonClasses,
                  )}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="w-full flex-1 overflow-y-auto px-6 py-6 min-h-0 custom-scrollbar overscroll-contain">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          // Botões sempre lado a lado (nunca empilhados); no celular dividem a largura.
          <div className="w-full flex flex-row items-center justify-end gap-2 sm:gap-3 px-4 sm:px-6 py-3 border-t border-purple-200 shrink-0 bg-white/50 backdrop-blur-md sticky bottom-0 z-10 [&>button]:flex-1 sm:[&>button]:flex-none">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default Modal;
